import React, { useState, useEffect } from 'react';
import { Button, Menu, MenuItem, ListItemText, CircularProgress } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useUserStore } from '../../store';
import { formatPrice } from '../../utils/vat';
import { withoutEmptyCustomerName } from '../../utils/customer';

const resolveTemplateVariables = (templateBody, maintenance, currentOrganization) => {
  const now = new Date();
  const dateStr = now.toLocaleDateString('fi-FI');

  const partsTotal = (maintenance?.parts || []).reduce(
    (sum, p) => sum + (parseFloat(p.line_total_incl_vat) || parseFloat(p.line_total) || 0),
    0
  );
  const jobsTotal = (maintenance?.jobs || []).reduce(
    (sum, j) => sum + (parseFloat(j.line_total_incl_vat) || parseFloat(j.line_total) || 0),
    0
  );
  const totalPrice = formatPrice(maintenance?.total_with_vat ?? (partsTotal + jobsTotal));

  const variables = {
    vehicle_reg: maintenance?.vehicle?.registration_number || '',
    vehicle_make: maintenance?.vehicle?.manufacturer || '',
    vehicle_model: maintenance?.vehicle?.model || '',
    organization_name: currentOrganization?.organization_name || '',
    organization_phone: currentOrganization?.organization_phone || '',
    total_price: totalPrice,
    date: dateStr,
    customer_name: maintenance?.customer?.name || '',
  };

  // A nameless customer gets "Hei," not "Hei ," (only a phone or email on the customer)
  return withoutEmptyCustomerName(templateBody, variables.customer_name).replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
};

let cachedTemplates = null;
let cacheOrgId = null;

function TemplatePicker({ maintenance, onSelect }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const currentOrganization = useUserStore((state) => state.currentOrganization);

  const fetchTemplates = async () => {
    const orgId = currentOrganization?.organization_id;
    if (cachedTemplates && cacheOrgId === orgId) {
      setTemplates(cachedTemplates);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get('/api/customer-note-templates/');
      cachedTemplates = response.data;
      cacheOrgId = orgId;
      setTemplates(response.data);
    } catch (err) {
      console.error('Failed to fetch templates', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchTemplates();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (template) => {
    const resolved = resolveTemplateVariables(template.body, maintenance, currentOrganization);
    onSelect(resolved);
    handleClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ArticleIcon />}
        onClick={handleClick}
        size="small"
        sx={{
          borderColor: 'secondary.main',
          color: 'secondary.main',
          borderRadius: '8px',
          fontWeight: 500,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          textTransform: 'none',
          '&:hover': {
            borderColor: 'secondary.dark',
            bgcolor: 'rgba(49, 130, 206, 0.04)',
          },
        }}
      >
        {t('Message template')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {loading ? (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            {t('Loading...')}
          </MenuItem>
        ) : templates.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary={t('No templates')} />
          </MenuItem>
        ) : (
          templates.map((template) => (
            <MenuItem key={template.id} onClick={() => handleSelect(template)}>
              <ListItemText primary={template.name} />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}

// Export for cache invalidation from settings
export const invalidateTemplateCache = () => {
  cachedTemplates = null;
  cacheOrgId = null;
};

export default TemplatePicker;
